import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useVault } from '../contexts/VaultContext';
import {
  createVault, unlockVault, hasVault,
  isPrivateKeyHex, isValidMnemonic, importFromHex, importFromMnemonic,
  signLoginNonce, signLoginNonceWithSecret, WrongPasswordError,
} from '../wallet';
import { requestLoginChallenge, loginWithKey, fetchAccountState } from '../services/wallet-api';
import { lookupVault, rememberVault } from '../services/vault-index';

export type AuthView = 'landing' | 'signin' | 'signup' | 'verify-email' | 'vault-password';

interface LandingPageProps {
  onNavigate: (view: AuthView) => void;
  initialView?: AuthView;
}

const Card: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
  <div className={`w-full ${wide ? 'max-w-[520px] lg:max-w-[560px]' : 'max-w-[460px] lg:max-w-[500px]'} px-4 sm:px-0`}>
    <div className="rounded-2xl sm:rounded-3xl overflow-hidden backdrop-blur-xl border border-gray-800/30 shadow-2xl relative card-glow">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-950/30 via-gray-900/70 to-teal-950/30 z-0" />
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-cyan-400/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-teal-400/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl" />
        <div className="absolute bottom-1/4 right-1/4 w-32 h-32 bg-teal-500/5 rounded-full blur-2xl" />
      </div>
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute top-8 left-0 w-full h-[1px] pulse-line bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
        <div className="absolute bottom-8 left-0 w-full h-[1px] pulse-line bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />
      </div>
      <div className="p-6 sm:p-8 lg:p-10 relative z-10 bg-black/10 backdrop-blur-sm">
        {children}
      </div>
    </div>
  </div>
);

const EyeToggle: React.FC<{ show: boolean; onToggle: () => void }> = ({ show, onToggle }) => (
  <button type="button" onClick={onToggle} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white hover:text-cyan-400 transition-colors">
    {show ? (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
    ) : (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
    )}
  </button>
);

const Spinner = () => (
  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const ErrorBar: React.FC<{ msg: string }> = ({ msg }) => (
  <div className="bg-red-500/[0.07] border border-red-500/20 rounded-lg px-3 py-2 mb-4">
    <p className="text-red-400 text-xs">{msg}</p>
  </div>
);

const FormTitle: React.FC<{ title: string; sub?: string }> = ({ title, sub }) => (
  <div className="mb-5">
    <h2 className="text-white text-lg font-semibold">{title}</h2>
    {sub && <p className="text-white text-xs mt-1">{sub}</p>}
  </div>
);

const Divider: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-center gap-3 mt-5">
    <div className="flex-1 h-px pulse-line bg-gradient-to-r from-transparent via-cyan-800/30 to-transparent" />
    <span className="text-white text-xs">{text}</span>
    <div className="flex-1 h-px pulse-line bg-gradient-to-r from-transparent via-cyan-800/30 to-transparent" />
  </div>
);

const PASSWORD_RULES = [
  { id: 'length', label: '8+', test: (p: string) => p.length >= 8 },
  { id: 'upper', label: 'A-Z', test: (p: string) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'a-z', test: (p: string) => /[a-z]/.test(p) },
  { id: 'number', label: '0-9', test: (p: string) => /\d/.test(p) },
];

const LandingPage: React.FC<LandingPageProps> = ({ onNavigate, initialView }) => {
  const { register, verifyEmail: verifyEmailApi, login, resendVerification, applySession } = useAuth();
  const { adoptSession } = useVault();

  const [view, setView] = useState<AuthView>(initialView || 'signin');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [privateKey, setPrivateKey] = useState('');
  const [resendCooldown, setResendCooldown] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  // Session obtained via the key, held aside while no vault exists yet: applying it right
  // away would leave the page before the user has chosen a password.
  const [pendingAuth, setPendingAuth] = useState<{ data: any; secret: string } | null>(null);
  // Sign-in asks for the email first, then for whatever that account can actually produce.
  // Guessing was what trapped people: someone who closed the tab during email verification
  // was shown a password field for a password that does not exist, and a key field for a
  // wallet not yet created — with no way back.
  const [signinStep, setSigninStep] = useState<'email' | 'password' | 'key'>('email');

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isValidUsername = (u: string) => /^[a-zA-Z0-9_-]{3,50}$/.test(u);
  const passwordValid = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const verifyCode = digits.join('');

  const go = (v: AuthView) => {
    setError(''); setPassword(''); setConfirmPassword('');
    setShowPassword(false); setShowConfirm(false);
    setPrivateKey('');
    setDigits(['', '', '', '', '', '']);
    setSigninStep('email');
    setView(v); onNavigate(v);
  };

  const handleDigitChange = (idx: number, val: string) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/\d/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    setError('');
    if (val && idx < 5) digitRefs.current[idx + 1]?.focus();
  };

  const handleDigitKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      digitRefs.current[idx - 1]?.focus();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length > 0) {
      e.preventDefault();
      const next = ['', '', '', '', '', ''];
      text.split('').forEach((c, i) => { next[i] = c; });
      setDigits(next);
      digitRefs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  // ── Handlers ───────────────────────────────────────────────
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidUsername(username) || !isValidEmail(email) || !inviteCode || !acceptTerms) return;
    setError(''); setLoading(true);
    const result = await register(username, email, inviteCode);
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    setPendingEmail(email);
    setView('verify-email'); onNavigate('verify-email');
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verifyCode.length !== 6) return;
    setError(''); setLoading(true);
    const result = await verifyEmailApi(pendingEmail || email, verifyCode);
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    go('signin');
  };

  // Step one of signing in: the email alone decides what to ask for next.
  //
  // The local vault wins over the server's answer — if this browser already holds the key
  // for that address, the password decrypts it here and the server is never consulted.
  const handleIdentify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) return;
    setError(''); setLoading(true);
    try {
      if (lookupVault(email)) { setSigninStep('password'); return; }

      const step = await fetchAccountState(email);
      if (step === 'key') { setSigninStep('key'); return; }
      if (step === 'password') { setSigninStep('password'); return; }

      if (step === 'verify') {
        // Email confirmed but no wallet, or never confirmed at all: in both cases a code
        // is the only credential this account can produce. This is the door that was
        // missing — without it these people had no way back in.
        const result = await resendVerification(email);
        if (result.error) { setError(result.error); return; }
        setPendingEmail(email);
        setDigits(['', '', '', '', '', '']);
        setView('verify-email'); onNavigate('verify-email');
        return;
      }

      setError('No account found with this email.');
    } catch (err) {
      setError((err as Error).message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // Sign-in. Three cases, in this order:
  //  1. this browser already holds the vault for that email -> the password decrypts it
  //     locally, and the key is what authenticates. Nothing is sent to the server.
  //  2. non-custodial account with no vault here -> the server answers NON_CUSTODIAL and we
  //     switch to asking for the key.
  //  3. still-custodial account -> classic password sign-in.
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email) || !password) return;
    setError(''); setLoading(true);

    const knownAccountId = lookupVault(email);
    if (knownAccountId && await hasVault(knownAccountId).catch(() => false)) {
      try {
        const session = await unlockVault(knownAccountId, password);
        const { nonce } = await requestLoginChallenge(email);
        const data = await loginWithKey(email, nonce, signLoginNonce(session, nonce));
        applySession(data);
        adoptSession(session);
      } catch (err) {
        setError(err instanceof WrongPasswordError
          ? 'Wrong password'
          : (err as Error).message || 'Could not sign in');
      } finally {
        setLoading(false);
      }
      return;
    }

    const result = await login(email, password);
    setLoading(false);
    if (result.error) {
      if (result.code === 'NON_CUSTODIAL') {
        // Migrated account: the key authenticates, not the password. Reachable when a
        // stale local vault points at an account that has since been re-created.
        setPassword(''); setError(''); setSigninStep('key');
        return;
      }
      const msg = (result.error || '').toLowerCase();
      if (msg.includes('verify') || msg.includes('unverified') || msg.includes('not verified')) {
        setPendingEmail(email); setView('verify-email'); onNavigate('verify-email'); return;
      }
      setError(result.error);
    }
  };

  // Fresh browser: the user pastes their key. We authenticate by signature, but hold the
  // session aside — the vault does not exist yet, a password has to be chosen first.
  const handleKeyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const secret = privateKey.trim();
    if (!isValidEmail(email) || (!isPrivateKeyHex(secret) && !isValidMnemonic(secret))) return;
    setError(''); setLoading(true);
    try {
      const { nonce } = await requestLoginChallenge(email);
      const data = await loginWithKey(email, nonce, signLoginNonceWithSecret(secret, nonce));

      // The key must match the account, otherwise we would create a vault that signs for
      // a different party and the mistake would only surface on the first transfer.
      const identity = isPrivateKeyHex(secret) ? importFromHex(secret) : importFromMnemonic(secret);
      if (data.user?.canton_party_id && !data.user.canton_party_id.endsWith(identity.fingerprint)) {
        setError('This key does not match the wallet on your account');
        return;
      }

      setPendingAuth({ data, secret });
      setPassword(''); setConfirmPassword('');
      setView('vault-password'); onNavigate('vault-password');
    } catch (err) {
      setError((err as Error).message || 'Could not sign in with this key');
    } finally {
      setLoading(false);
    }
  };

  // Choosing the password that protects the vault on THIS device. It never goes to the server.
  const handleCreateVaultPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingAuth || !passwordValid || !passwordsMatch) return;
    setError(''); setLoading(true);
    try {
      const accountId = pendingAuth.data.user.id;
      await createVault(accountId, pendingAuth.secret, password, { overwrite: true });
      rememberVault(email, accountId);
      const session = await unlockVault(accountId, password);
      applySession(pendingAuth.data);
      adoptSession(session);
      setPendingAuth(null);
    } catch (err) {
      setError((err as Error).message || 'Could not set up your wallet on this device');
    } finally {
      setLoading(false);
    }
  };


  const resendTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(resendTimerRef.current), []);

  const handleResend = async () => {
    if (resendCooldown) return;
    const target = pendingEmail || email;
    if (!target) return;
    setResendCooldown(true);
    const result = await resendVerification(target);
    if (result.error) { setError(result.error); setResendCooldown(false); return; }
    clearTimeout(resendTimerRef.current);
    resendTimerRef.current = setTimeout(() => setResendCooldown(false), 60000);
  };

  const inp = 'w-full bg-black/30 rounded-xl p-3 text-sm text-white placeholder-gray-400 outline-none border border-gray-700/30 focus:border-cyan-500/50 focus:bg-black/40 transition-all backdrop-blur-sm';
  const btnOn = 'bg-cyan-500/20 border-2 border-cyan-400/60 text-cyan-300 font-medium hover:bg-cyan-500/30 hover:border-cyan-300 hover:text-white hover:shadow-[0_0_25px_rgba(34,211,238,0.2)] transition-all';
  const btnOff = 'bg-cyan-950/20 border-2 border-cyan-800/25 text-cyan-700/70 cursor-not-allowed';

  // ── Views ──────────────────────────────────────────────────
  const renderCard = () => {
    if (view === 'signin') {
      const secret = privateKey.trim();
      const secretOk = isPrivateKeyHex(secret) || isValidMnemonic(secret);
      const onEmail = signinStep === 'email';
      const canSubmit = !loading && isValidEmail(email)
        && (onEmail ? true : signinStep === 'password' ? !!password : secretOk);

      const subtitle = onEmail ? 'Enter your email to continue'
        : signinStep === 'password' ? 'Unlock your wallet on this device'
        : 'Sign in with your wallet key';

      const backToEmail = () => {
        setSigninStep('email'); setPassword(''); setPrivateKey(''); setError('');
      };

      return (
        <Card>
          <FormTitle title="Sign in" sub={subtitle} />
          {error && <ErrorBar msg={error} />}
          <form onSubmit={onEmail ? handleIdentify : signinStep === 'password' ? handleLogin : handleKeyLogin}>
            <input type="email" value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); if (!onEmail) backToEmail(); }}
              placeholder="Email address" className={`${inp} ${onEmail ? 'mb-5' : 'mb-3'}`}
              autoComplete="email" autoFocus={onEmail} />

            {signinStep === 'password' && (
              <>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="Password" className={`${inp} pr-10`}
                    autoComplete="current-password" autoFocus />
                  <EyeToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                </div>
                <div className="flex justify-between items-center mt-2 mb-5">
                  <button type="button" onClick={backToEmail}
                    className="text-white hover:text-cyan-400 text-xs transition-colors">Use another email</button>
                  <button type="button" onClick={() => { setSigninStep('key'); setPassword(''); setError(''); }}
                    className="text-white hover:text-cyan-400/70 text-xs transition-colors">Forgot password?</button>
                </div>
              </>
            )}

            {signinStep === 'key' && (
              <>
                <textarea value={privateKey} onChange={(e) => { setPrivateKey(e.target.value); setError(''); }}
                  rows={3} spellCheck={false} autoComplete="off" autoFocus
                  placeholder="Recovery key (64 characters) or your 24-word phrase"
                  className={`${inp} font-mono text-xs resize-none`} />
                <p className="text-white text-[13px] mt-2 leading-relaxed">
                  This browser doesn't hold your wallet yet. Your key proves who you are — it is
                  never sent to us. You'll choose a password for this device next.
                </p>
                <div className="flex justify-start mt-2 mb-5">
                  <button type="button" onClick={backToEmail}
                    className="text-white hover:text-cyan-400 text-xs transition-colors">Use another email</button>
                </div>
              </>
            )}

            <button type="submit" disabled={!canSubmit}
              className={`w-full font-medium py-2.5 rounded-xl text-sm ${!canSubmit ? btnOff : btnOn}`}>
              {loading
                ? <span className="flex items-center justify-center gap-2"><Spinner /> {onEmail ? 'Checking...' : 'Signing in...'}</span>
                : 'Continue'}
            </button>
          </form>
          <Divider text="New to Nuxaris?" />
          <button onClick={() => go('signup')} className="w-full mt-4 py-2.5 rounded-xl text-sm font-medium text-cyan-400/70 border border-cyan-800/30 hover:border-cyan-600/40 hover:text-cyan-300 bg-cyan-950/10 transition-all">
            Create an account
          </button>
        </Card>
      );
    }

    if (view === 'vault-password') {
      return (
        <Card>
          <FormTitle title="Protect your wallet" sub="Choose a password for this device" />
          {error && <ErrorBar msg={error} />}
          <form onSubmit={handleCreateVaultPassword}>
            <div className="relative mb-3">
              <input type={showPassword ? 'text' : 'password'} value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="New password" className={`${inp} pr-10`} autoComplete="new-password" autoFocus />
              <EyeToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {PASSWORD_RULES.map((r) => (
                <span key={r.id} className={`px-2 py-0.5 rounded-md text-[13px] border ${
                  r.test(password) ? 'text-cyan-300 border-cyan-500/40 bg-cyan-500/10' : 'text-white border-gray-700/40'
                }`}>{r.label}</span>
              ))}
            </div>
            <div className="relative">
              <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Confirm password" className={`${inp} pr-10`} autoComplete="new-password" />
              <EyeToggle show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} />
            </div>
            <p className="text-white text-[13px] mt-3 mb-5 leading-relaxed">
              This password encrypts your key on this device only. We never receive it, and we
              cannot reset it — on another browser you'll sign in with your key again.
            </p>
            <button type="submit" disabled={!passwordValid || !passwordsMatch || loading}
              className={`w-full font-medium py-2.5 rounded-xl text-sm ${!passwordValid || !passwordsMatch || loading ? btnOff : btnOn}`}>
              {loading ? <span className="flex items-center justify-center gap-2"><Spinner /> Setting up...</span> : 'Enter Nuxaris'}
            </button>
          </form>
        </Card>
      );
    }

    if (view === 'signup') {
      const canSubmit = isValidUsername(username) && isValidEmail(email) && inviteCode.length > 0 && acceptTerms && !loading;

      /* Why the button is off.
         Four conditions gated this button and the form named none of them, so a form that
         looked complete simply refused to submit with nothing to read. That is a dead end, and
         it produced real support tickets — people assumed their invite code was broken when the
         terms box was the thing left unticked.
         Nothing is said until something has been typed: an untouched form being told what it is
         missing is nagging, not help. */
      const started = username.length > 0 || email.length > 0 || inviteCode.length > 0;
      const missing = !started || canSubmit || loading ? null
        : !isValidUsername(username)
          ? (username.length === 0
              ? 'Choose a username'
              : 'Username: 3–50 characters, letters, numbers, - and _ only')
        : !isValidEmail(email)
          ? (email.length === 0 ? 'Enter your email address' : 'That email address does not look right')
        : inviteCode.length === 0 ? 'Enter your invite code'
        : !acceptTerms ? 'Tick the box above to accept the Terms and Privacy Policy'
        : null;
      return (
        <Card wide>
          <FormTitle title="Create account" />
          {error && <ErrorBar msg={error} />}
          <form onSubmit={handleRegister}>
            <div className="grid grid-cols-2 gap-2.5 mb-2.5">
              <input type="text" value={username} onChange={(e) => { setUsername(e.target.value); setError(''); }}
                placeholder="Username" className={inp} autoComplete="username" />
              <input type="text" value={inviteCode} onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setError(''); }}
                placeholder="Invite code" className={`${inp} uppercase tracking-wide`} autoComplete="off" />
            </div>
            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="Email address" className={`${inp} mb-2.5`} autoComplete="email" />
            <div className="rounded-xl border border-gray-700/40 bg-white/[0.02] p-3 mt-2.5">
              <p className="text-white text-[13px] leading-relaxed">
                No password here. Right after you verify your email you'll create your wallet and
                choose a password that protects it on this device — it never reaches our servers.
              </p>
            </div>
            <label className="flex items-center gap-2 mt-4 mb-4 cursor-pointer">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-600 bg-transparent text-cyan-500 focus:ring-cyan-500/30 cursor-pointer accent-cyan-500" />
              <span className="text-white text-xs">
                I agree to the <span className="text-white">Terms</span> and <span className="text-white">Privacy Policy</span>
              </span>
            </label>
            <button type="submit" disabled={!canSubmit}
              className={`w-full font-medium py-2.5 rounded-xl text-sm ${canSubmit ? btnOn : btnOff}`}>
              {loading ? <span className="flex items-center justify-center gap-2"><Spinner /> Creating...</span> : 'Create account'}
            </button>
            {missing && (
              <p className="mt-2.5 text-center text-amber-400/90 text-[13px] leading-relaxed">
                {missing}
              </p>
            )}
          </form>
          <Divider text="Already registered?" />
          <button onClick={() => go('signin')} className="w-full mt-4 py-2.5 rounded-xl text-sm font-medium text-cyan-400/70 border border-cyan-800/30 hover:border-cyan-600/40 hover:text-cyan-300 bg-cyan-950/10 transition-all">
            Sign in instead
          </button>

          {/* Without an invite code, signing up is a dead end: give both routes to get one. */}
          <div className="mt-5 pt-4 border-t border-gray-800/40 text-center">
            <p className="text-white text-[13px] leading-relaxed">
              No invite code?{' '}
              <a href="https://www.nuxaris.com/" target="_blank" rel="noopener noreferrer"
                className="text-cyan-400/80 hover:text-cyan-300 underline underline-offset-2 transition-colors">
                Request access on our website
              </a>
              {' '}or reach us here:
            </p>
            <div className="flex items-center justify-center gap-2 mt-2.5">
              <a href="https://discord.gg/nzMQCVxMPw" target="_blank" rel="noopener noreferrer" title="Join our Discord"
                className="w-8 h-8 rounded-lg bg-white/[0.03] border border-gray-700/40 flex items-center justify-center text-white hover:text-white hover:bg-white/[0.06] hover:border-cyan-500/30 transition-all">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
              </a>
              <a href="https://x.com/Nuxaris" target="_blank" rel="noopener noreferrer" title="Follow us on X"
                className="w-8 h-8 rounded-lg bg-white/[0.03] border border-gray-700/40 flex items-center justify-center text-white hover:text-white hover:bg-white/[0.06] hover:border-cyan-500/30 transition-all">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </div>
        </Card>
      );
    }

    if (view === 'verify-email') {
      const target = pendingEmail || email;
      return (
        <Card>
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
            </div>
            <h2 className="text-white text-lg font-semibold">Check your email</h2>
            <p className="text-white text-xs mt-1.5 leading-relaxed">
              We sent a 6-digit code to<br /><span className="text-white">{target}</span>
            </p>
          </div>
          {error && <ErrorBar msg={error} />}
          <form onSubmit={handleVerifyEmail}>
            <div className="flex justify-center gap-2 sm:gap-2.5 mb-5" onPaste={handleDigitPaste}>
              {digits.map((d, i) => (
                <input key={i} ref={(el) => { digitRefs.current[i] = el; }}
                  type="text" inputMode="numeric" maxLength={1} value={d}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(i, e)}
                  className={`w-10 h-12 sm:w-11 sm:h-13 text-center text-lg font-mono rounded-lg bg-black/30 backdrop-blur-sm border ${d ? 'border-cyan-500/50 text-white bg-cyan-950/20' : 'border-gray-700/30 text-white'} outline-none focus:border-cyan-400/60 focus:bg-cyan-950/20 transition-all`} />
              ))}
            </div>
            <button type="submit" disabled={verifyCode.length !== 6 || loading}
              className={`w-full font-medium py-2.5 rounded-xl text-sm ${verifyCode.length !== 6 || loading ? btnOff : btnOn}`}>
              {loading ? <span className="flex items-center justify-center gap-2"><Spinner /> Verifying...</span> : 'Verify email'}
            </button>
          </form>
          <div className="flex items-center justify-center gap-3 mt-4 text-xs">
            <button onClick={handleResend} disabled={resendCooldown}
              className={resendCooldown ? 'text-white/50 cursor-not-allowed' : 'text-white hover:text-cyan-400 transition-colors'}>
              {resendCooldown ? 'Sent' : 'Resend code'}
            </button>
            <span className="text-white">|</span>
            <button onClick={() => go('signin')} className="text-white hover:text-cyan-400 transition-colors">Back</button>
          </div>
        </Card>
      );
    }


    return null;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <nav className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-5 flex justify-between items-center relative z-20">
        <button onClick={() => go('signin')} className="flex items-center gap-0.5 hover:opacity-80 transition-opacity">
          <img src="/logo.webp" alt="Nuxaris" className="w-10 h-10 sm:w-11 sm:h-11" />
          <span className="text-white text-xl sm:text-2xl font-bold tracking-wider font-heading">Nuxaris</span>
        </button>
        <div className="flex items-center gap-4">
          {view === 'signin' && (
            <button onClick={() => go('signup')} className="text-white hover:text-white text-xs sm:text-sm font-medium transition-colors">Create account</button>
          )}
          {view === 'signup' && (
            <button onClick={() => go('signin')} className="text-white hover:text-white text-xs sm:text-sm font-medium transition-colors">Sign in</button>
          )}
          {view !== 'signin' && view !== 'signup' && (
            <button onClick={() => go('signin')} className="text-white hover:text-white text-xs sm:text-sm transition-colors">Sign in</button>
          )}
        </div>
      </nav>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        {renderCard()}
        <p className="text-white text-[13px] mt-8 tracking-wide">Built on Canton Network</p>
      </main>

      <footer className="w-full border-t border-gray-800/30 mt-auto">
        <div className="px-4 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-[13px] sm:text-xs text-white">
            <span>&copy; 2026 Nuxaris</span>
            <a href="https://nuxaris.notion.site/doc" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Docs</a>
            <a href="https://discord.gg/nzMQCVxMPw" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Support</a>
          </div>
          <div className="flex items-center gap-2.5">
            <a href="https://x.com/Nuxaris" target="_blank" rel="noopener noreferrer" className="text-white hover:text-cyan-400 transition-colors">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://discord.gg/nzMQCVxMPw" target="_blank" rel="noopener noreferrer" title="Join our Discord" className="text-white hover:text-cyan-400 transition-colors">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
